import { Flex, Radio, RadioGroup, Stack, Text, VStack } from '@chakra-ui/react'
import {
  DropdownField,
  DropdownOption,
  DropdownOptionGroup,
  DropdownOptionItem,
  InputSize,
  isDropdownOptionItem,
} from '@hb/shared'
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react'
import { InputElement, InputProps } from '../../../../types/fields'
import { getInputSizeTokens, useInputSize } from '../Shared/sizes'
import { InputRef } from '../types'

const noneOption: DropdownOptionItem = {
  text: 'None',
  id: 'NULL',
}

const addNoneOption = (field: DropdownField, options: DropdownOption<any>[]) => {
  if (field.withNoneOption) {
    return [...options, noneOption]
  }
  return options
}

const RadioOptionGroup: React.FC<{
  option: DropdownOptionGroup<any>
  field: DropdownField
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  readOnly?: boolean
  size: InputSize
}> = ({ option: { title, options }, field, value, onChange, disabled, readOnly, size }) => (
  <VStack align="flex-start" spacing={2} w="100%">
    <Text fontSize={getInputSizeTokens(size).placeholderFontSize} fontWeight={600} color="gray.600">
      {title}
    </Text>
    <Stack spacing={2} pl={4} w="100%">
      {options.map(opt => {
        if (isDropdownOptionItem(opt)) {
          return (
            <RadioOptionItem
              key={opt.id}
              option={opt}
              field={field}
              value={value}
              onChange={onChange}
              disabled={disabled}
              readOnly={readOnly}
              size={size}
            />
          )
        }
        return null
      })}
    </Stack>
  </VStack>
)

const RadioOptionItem: React.FC<{
  option: DropdownOptionItem
  field: DropdownField
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  readOnly?: boolean
  size: InputSize
}> = ({ option, field, value, onChange, disabled, readOnly, size }) => {
  const tokens = getInputSizeTokens(size)
  const isSelected = value === option.id
  const displayContent = field.renderOption ? field.renderOption(option) : option.text

  return (
    <Radio
      value={option.id}
      isChecked={isSelected}
      onChange={() => {
        if (!disabled && !readOnly) {
          onChange(option.id)
        }
      }}
      bg="white"
      _dark={{ bg: 'gray.900' }}
      size={tokens.checkboxSize}
      isDisabled={disabled || readOnly}
      colorScheme="sage">
      {typeof displayContent === 'string' ? (
        <Text fontSize={tokens.fontSize} color="#333333" fontFamily="Hero-New">
          {displayContent}
        </Text>
      ) : (
        displayContent
      )}
    </Radio>
  )
}

export const RadioInput: InputElement<DropdownField> = forwardRef<
  InputRef,
  InputProps<DropdownField>
>(({ input, field, style, disabled, readOnly, size: sizeProp }, ref) => {
  const { options } = field
  const direction = field.radioDirection ?? 'row'
  const { onChange, value, onBlur, onFocus, name } = input
  const size = useInputSize(sizeProp ?? field?.size)
  const radioGroupRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => ({
    focus: () => {
      // Focus the first radio button in the group
      const firstRadio = radioGroupRef.current?.querySelector(
        'input[type="radio"]',
      ) as HTMLElement | null
      firstRadio?.focus()
      onFocus()
    },
    blur: () => {
      const activeElement = radioGroupRef.current?.querySelector(
        'input[type="radio"]:checked',
      ) as HTMLElement | null
      activeElement?.blur()
      onBlur()
    },
  }))

  const allOptions = useMemo(() => addNoneOption(field, options), [field, options])
  // const flattenedOptions = useMemo(() => getAllOptions(allOptions), [allOptions])
  const handleChange = useCallback(
    (selectedValue: string) => {
      onChange(selectedValue)
      onBlur()
    },
    [onChange, onBlur],
  )

  const currentValue = useMemo(() => {
    if (value === undefined || value === null) return ''
    return value
  }, [value])

  return (
    <Flex data-child-name={name} flexFlow="column" w="100%">
      <RadioGroup
        ref={radioGroupRef}
        value={currentValue}
        onChange={handleChange}
        name={name}
        isDisabled={disabled || readOnly}
        style={style}>
        <Flex
          px={1}
          bg="transparent"
          borderRightRadius={6}
          h="100%"
          flexFlow={direction === 'column' ? 'column' : 'row wrap'}
          align={direction === 'column' ? 'flex-start' : 'center'}
          columnGap={5}
          rowGap={1}>
          {allOptions.map((option, index) => {
            if (isDropdownOptionItem(option)) {
              return (
                <RadioOptionItem
                  key={option.id}
                  option={option}
                  field={field}
                  value={currentValue}
                  onChange={handleChange}
                  disabled={disabled}
                  readOnly={readOnly}
                  size={size}
                />
              )
            } else {
              return (
                <RadioOptionGroup
                  key={`group-${index}-${option.title}`}
                  option={option}
                  field={field}
                  value={currentValue}
                  onChange={handleChange}
                  disabled={disabled}
                  readOnly={readOnly}
                  size={size}
                />
              )
            }
          })}
        </Flex>
      </RadioGroup>
    </Flex>
  )
})
